import React, { useMemo, useState } from 'react';
import { ArrowRightIcon, SearchIcon, XIcon } from 'lucide-react';
import { TOOLS, TOOL_FILTERS, TOOLS_DIRECTORY } from '../data/tools';
import { ENTERPRISE_DEMO_URL } from '../data/dgpt';
import { usePageMeta, canonicalUrl } from '../hooks/usePageMeta';
import { Eyebrow } from '../components/ui/Eyebrow';
import { CtaButton } from '../components/ui/CtaButton';
import { ToolCard } from '../components/tools/ToolCard';
import { cn } from '../src/lib/utils';
import { trackEvent } from '../utils/analytics';
import type { ToolCategory } from '../types/tools';
type FilterId = 'all' | ToolCategory;
export function ToolsDirectory() {
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterId>('all');
  usePageMeta({
    title: TOOLS_DIRECTORY.title,
    description: TOOLS_DIRECTORY.description,
    path: '/tools',
    jsonLd: [{
      '@type': 'BreadcrumbList',
      itemListElement: [{
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl('/')
      }, {
        '@type': 'ListItem',
        position: 2,
        name: 'Tools',
        item: canonicalUrl('/tools')
      }]
    }]
  });
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    return TOOLS.filter(tool => {
      if (filter !== 'all' && !tool.categories.includes(filter)) return false;
      if (!term) return true;
      const haystack = [tool.name, tool.cardDescription, tool.hero.supporting, ...tool.seo.keywords, ...tool.outputs.map(o => o.title)].join(' ').toLowerCase();
      return term.split(/\s+/).every(word => haystack.includes(word));
    });
  }, [query, filter]);
  const browsing = filter === 'all' && !query.trim();
  const featured = TOOLS.filter(tool => tool.featured);
  const gridTools = browsing ? TOOLS.filter(tool => !tool.featured) : results;
  return <main id="main" className="bg-ink" data-id="element-2123" data-mp="2wazbzndbbi1t">
      <section aria-labelledby="tools-heading" className="relative overflow-hidden border-b border-hairline-soft" data-id="element-2124" data-mp="30eoz08ec9rkq">
        <div className="sf-grid-texture sf-fade-mask pointer-events-none absolute inset-0" aria-hidden="true" data-id="element-2125" data-mp="d6r7b7cfu2jj" />
        <div className="relative mx-auto w-full max-w-[1320px] px-4 pb-14 pt-12 sm:px-6 lg:px-8 lg:pb-16 lg:pt-16" data-id="element-2126" data-mp="85bmnge69y1o">
          <Eyebrow data-id="element-2127" data-mp="47dwaa8o15wp">DiligenceGPT™ Tools</Eyebrow>
          <h1 id="tools-heading" className="mt-4 max-w-3xl text-balance text-[40px] font-semibold leading-[1.02] tracking-[-0.04em] text-text-primary sm:text-[56px] lg:text-[64px]" data-id="element-2128" data-mp="3vcckqn2ccj88">
            {TOOLS_DIRECTORY.h1}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-text-secondary sm:text-lg" data-id="element-2129" data-mp="rx894rsziugy">{TOOLS_DIRECTORY.intro}</p>

          <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" data-id="element-2130" data-mp="i5kll1zjtxkr">
            <div className="relative w-full lg:max-w-md" data-id="element-2131" data-mp="1fkyjmvv1zs31">
              <label htmlFor="tool-search" className="sr-only" data-id="element-2132" data-mp="1fmjd0i5mt4zb">
                Search tools by name or task
              </label>
              <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-text-tertiary" aria-hidden="true" data-id="element-2133" data-mp="1i54nl3jo3ijl" />
              <input id="tool-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search by tool or task — e.g. memo, runway, competitors" className="h-12 w-full rounded-xl border border-hairline bg-panel pl-10 pr-10 text-sm text-text-primary placeholder:text-text-tertiary focus:border-[#D8D4EA] focus:outline-none focus:ring-2 focus:ring-lime/30" data-id="element-2134" data-mp="1n4hfi2pebft1" />
              {query ? <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-2135" data-mp="2fsn26v00fevi">
                  <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-2136" data-mp="2759506ed9sh9" />
                </button> : null}
            </div>

            <div role="group" aria-label="Filter tools by stage" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0" data-id="element-2137" data-mp="2kvckmeiqqnxq">
              {TOOL_FILTERS.map(item => {
              const active = filter === item.id;
              return <button key={item.id} type="button" aria-pressed={active} onClick={() => setFilter(item.id)} className={cn('min-h-[40px] shrink-0 rounded-full border px-4 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime focus-visible:ring-offset-2 focus-visible:ring-offset-ink', active ? 'border-lime bg-lime text-ink' : 'border-hairline bg-panel text-text-secondary hover:border-[#D8D4EA] hover:text-text-primary')} data-id="element-2138" data-mp="1o6mygc02z95y">
                    {item.label}
                  </button>;
            })}
            </div>
          </div>
        </div>
      </section>

      {browsing ? <section aria-labelledby="featured-heading" className="border-b border-hairline-soft bg-surface" data-id="element-2139" data-mp="1e1og449cihls">
          <div className="mx-auto w-full max-w-[1320px] px-4 py-14 sm:px-6 lg:px-8" data-id="element-2140" data-mp="1i4bfqrc9d6tj">
            <h2 id="featured-heading" className="text-xs font-semibold uppercase tracking-[0.1em] text-text-secondary" data-id="element-2141" data-mp="14cmzqcyw1y7a">
              Start here
            </h2>
            <ul className="mt-6 grid gap-4 md:grid-cols-3" data-id="element-2142" data-mp="199n50lnwywxc">
              {featured.map(tool => <li key={tool.slug} data-id="element-2143" data-mp="1wyzdtkfbx2z4">
                  <ToolCard tool={tool} variant="featured" badge="Featured" onNavigate={() => trackEvent('tool_card_click', {
              tool: tool.slug,
              placement: 'directory_featured'
            })} data-id="element-2144" data-mp="21sao5vjco1vs" />
                </li>)}
            </ul>
          </div>
        </section> : null}

      <section aria-labelledby="all-tools-heading" className="border-b border-hairline-soft" data-id="element-2145" data-mp="1wqihfs478xpw">
        <div className="mx-auto w-full max-w-[1320px] px-4 py-14 sm:px-6 lg:px-8 lg:py-16" data-id="element-2146" data-mp="1wsuubqv3ohw3">
          <div className="flex items-baseline justify-between gap-4" data-id="element-2147" data-mp="1sqq5d5flk9fs">
            <h2 id="all-tools-heading" className="text-xs font-semibold uppercase tracking-[0.1em] text-text-secondary" data-id="element-2148" data-mp="1nwly0l2f5ze6">
              {browsing ? 'More tools' : 'Results'}
            </h2>
            <p className="text-xs text-text-tertiary" aria-live="polite" data-id="element-2149" data-mp="1e1btnqojtfx8">
              {browsing ? `${TOOLS.length} tools` : `${results.length} of ${TOOLS.length} tools`}
            </p>
          </div>

          {gridTools.length > 0 ? <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-id="element-2150" data-mp="151ou7afdqk6t">
              {gridTools.map(tool => <li key={tool.slug} data-id="element-2151" data-mp="3knhx8yrarwrr">
                  <ToolCard tool={tool} onNavigate={() => trackEvent('tool_card_click', {
              tool: tool.slug,
              placement: 'directory_grid'
            })} data-id="element-2152" data-mp="3kmg8jrhczbrk" />
                </li>)}
            </ul> : <div className="mt-6 flex flex-col items-center rounded-2xl border border-dashed border-hairline bg-surface px-6 py-14 text-center" data-id="element-2153" data-mp="3ukcu7d7x9p3x">
              <p className="text-base font-semibold text-text-primary" data-id="element-2154" data-mp="31x8kww1sqomc">No tools match “{query}”.</p>
              <p className="mt-1 text-sm text-text-secondary" data-id="element-2155" data-mp="36wcvnr4dcues">Try a task like “memo”, “runway” or “competitors”.</p>
              <button type="button" onClick={() => {
            setQuery('');
            setFilter('all');
          }} className="mt-5 inline-flex min-h-[44px] items-center rounded-lg border border-hairline bg-panel px-5 text-sm font-semibold text-text-primary hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime" data-id="element-2156" data-mp="3bomopg0s6ala">
                Show all tools
              </button>
            </div>}
        </div>
      </section>

      <section aria-labelledby="tools-demo-heading" className="bg-surface" data-id="element-2157" data-mp="1li9wc5v9eag6">
        <div className="mx-auto flex w-full max-w-[1320px] flex-col items-start justify-between gap-6 px-4 py-14 sm:px-6 md:flex-row md:items-center lg:px-8" data-id="element-2158" data-mp="1gk8d9kxu2b4p">
          <div data-id="element-2159" data-mp="1qkirn8vltolg">
            <h2 id="tools-demo-heading" className="text-xl font-semibold tracking-[-0.02em] text-text-primary" data-id="element-2160" data-mp="1qio2tjh7pcc3">
              Want to see the tools working together on your thesis?
            </h2>
            <p className="mt-1 text-sm text-text-secondary" data-id="element-2161" data-mp="20allfbajv6ku">A personalized walkthrough of DiligenceGPT™ with your fund’s criteria.</p>
          </div>
          <CtaButton href={ENTERPRISE_DEMO_URL} event="enterprise_demo_click" eventParams={{
          placement: 'tools_directory'
        }} size="lg" className="w-full md:w-auto" data-id="element-2162" data-mp="2e6z3alizx9zu">
            Book an Enterprise Demo
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-2163" data-mp="2xv3j624hd5ia" />
          </CtaButton>
        </div>
      </section>
    </main>;
}